import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Building2, MapPin, Loader2, Lock, ArrowRight } from "lucide-react";

const typeColor = (t) =>
  ({ "Full-time": "bg-blue-600", Internship: "bg-amber-600", Remote: "bg-emerald-600" }[t] || "bg-slate-600");

export default function Browse() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [courses, setCourses] = useState(null);
  const [jobs, setJobs] = useState(null);
  useSeo({
    title: "Job-Ready Courses & Openings | Classroom to Corporate",
    description: "Browse industry-designed, placement-linked courses and live job openings for students and fresh graduates from Classroom to Corporate, Ahmedabad.",
    jsonLd: jobs?.length ? {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Live job openings",
      itemListElement: jobs.map((j, i) => ({ "@type": "ListItem", position: i + 1, url: `${window.location.origin}/browse/jobs/${j.job_id}`, name: `${j.title} — ${j.company}` })),
    } : undefined,
  });

  useEffect(() => {
    api.get("/public/courses").then(({ data }) => setCourses(data)).catch(() => setCourses([]));
    api.get("/public/jobs").then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  }, []);

  const signIn = () => navigate("/login");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="browse-brand">
            <img src="/logo.png" alt="Classroom to Corporate" className="h-9 w-9 object-contain" />
            <span className="font-heading text-lg font-bold text-slate-900 hidden sm:inline">Classroom to <span className="text-amber-600">Corporate</span></span>
          </button>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button onClick={signIn} className="bg-blue-600 hover:bg-blue-700" data-testid="browse-signin">{t("browse.signIn")}</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10" data-testid="browse-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">{t("browse.title")}</h1>
          <p className="mt-2 text-slate-500">{t("browse.subtitle")}</p>
        </div>

        <Tabs defaultValue={params.get("tab") === "jobs" ? "jobs" : "courses"}>
          <TabsList>
            <TabsTrigger value="courses" data-testid="browse-tab-courses">{t("browse.tabCourses")}</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="browse-tab-jobs">{t("browse.tabJobs")}</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-6">
            {courses === null ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((c) => (
                  <Card key={c.course_id} data-testid="browse-course-card" onClick={() => navigate(`/browse/courses/${c.course_id}`)} className="cursor-pointer overflow-hidden border-slate-200 flex flex-col transition-all hover:shadow-lg hover:-translate-y-1">
                    <div className="h-40 overflow-hidden">
                      <img src={c.thumbnail} alt="" className="h-full w-full object-cover" />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <Badge variant="outline" className="w-fit mb-2">{c.category}</Badge>
                      <h3 className="font-heading text-lg font-bold text-slate-900">{c.title}</h3>
                      <p className="mt-1.5 text-sm text-slate-500 line-clamp-2">{c.description}</p>
                      <p className="mt-3 text-xs text-slate-400">{c.lesson_count} {t("common.lessons")} • {c.level} • {c.instructor}</p>
                      <Button variant="outline" className="mt-4" data-testid="browse-course-cta">
                        {t("publicCourse.viewSyllabus")} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            {jobs === null ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {jobs.map((j) => (
                  <Card key={j.job_id} data-testid="browse-job-card" className="border-slate-200 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-heading font-bold text-slate-900"><Link to={`/browse/jobs/${j.job_id}`} className="hover:text-amber-700 hover:underline" data-testid="browse-job-title-link">{j.title}</Link></h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                          <Building2 className="h-3.5 w-3.5" /> {j.company}
                          <span className="text-slate-300">•</span>
                          <MapPin className="h-3.5 w-3.5" /> {j.location}
                        </p>
                      </div>
                      <Badge className={typeColor(j.type)}>{j.type}</Badge>
                    </div>
                    <p className="mt-3 text-sm text-slate-600 line-clamp-2">{j.description}</p>
                    {j.salary && <p className="mt-3 inline-block rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{j.salary}</p>}
                    <Button asChild variant="ghost" className="mt-4 w-full text-amber-700 hover:text-amber-800" data-testid="browse-job-details">
                      <Link to={`/browse/jobs/${j.job_id}`}>{t("publicJob.view")} <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
                    </Button>
                    <Button onClick={signIn} variant="outline" className="mt-2 w-full" data-testid="browse-job-cta">
                      <Lock className="mr-1.5 h-3.5 w-3.5" /> {t("browse.signInToApply")}
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-12 rounded-2xl bg-blue-600 px-8 py-10 text-center text-white">
          <h2 className="font-heading text-2xl font-extrabold">{t("browse.ctaTitle")}</h2>
          <p className="mt-2 text-blue-100">{t("browse.ctaSubtitle")}</p>
          <Button onClick={signIn} className="mt-5 bg-white text-blue-700 hover:bg-blue-50" data-testid="browse-bottom-cta">
            {t("browse.signIn")} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </main>
      <WhatsAppButton />
    </div>
  );
}
